
import React, { useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';

import { trackPageview } from '@cvt/tracking';
import { useScrollTop } from '@cvt/hooks/useScrollTop';
import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import { AuthContext } from '@modules/Auth/contexts';
import { Authenticated, NotAuthenticated } from '@modules/Auth/components';

import config from '@shared/config';

import { Signup } from './Auth/Signup';
import { Login } from './Auth/Login';
import { ResetPassword } from './Auth/ResetPassword';
import { Profile } from './Users/Profile';
import { Disciplines } from './Disciplines/Disciplines';
import { ViewDiscipline } from './Disciplines/ViewDiscipline';
import { Majors } from './Majors/Majors';
import { ViewMajor } from './Majors/ViewMajor';
import { Universities } from './Universities/Universities';
import { ViewUniversity } from './Universities/ViewUniversity';
import { SwipableAssertions } from './Assertions/SwipableAssertions';
import { Onboarding } from './Users/Onboarding';
import { FeedbackSession } from './Feedback/FeedbackSession';
import { UnderstandMatches } from './Matches/UnderstandMatches';


export const Root = () => {
  const router = useRouter();
  const location = useLocation();
  const { loading } = useContext(AuthContext);

  useScrollTop();

  React.useEffect(() => {
    trackPageview();
  }, [location]);

  if (loading) {
    return <BodyLoading height="100%"/>;
  }

  return (
    <ErrorBoundary>
      <NotAuthenticated>
        <Routes>
          <Route path={router.auth.login.path} element={<Login/>}/>
          <Route path={router.auth.signup(':companySlug?').path} element={<Signup/>}/>
          <Route path={router.auth.resetPassword().path} element={<ResetPassword/>}/>
          <Route path="*" element={<Navigate to={router.auth.login.path} replace/>}/>
        </Routes>
      </NotAuthenticated>
      <Authenticated>
        <Routes>
          <Route path={router.home.path} element={<SwipableAssertions/>}/>
          <Route path={router.onboarding.path} element={<Onboarding/>}/>
          <Route path={router.feedback.path} element={<FeedbackSession/>}/>
          {config.featureFlags.understandMatches && (
            <Route path={router.understandMatches.path} element={<UnderstandMatches/>}/>
          )}
          <Route path={router.disciplines.path} element={<Disciplines/>}/>
          <Route path={`${router.disciplines.view().path}/*`} element={<ViewDiscipline/>}/>
          <Route path={router.majors.path} element={<Majors/>}/>
          <Route path={`${router.majors.view().path}/*`} element={<ViewMajor/>}/>
          <Route path={router.universities.path} element={<Universities/>}/>
          <Route path={`${router.universities.view().path}/*`} element={<ViewUniversity/>}/>
          <Route path={router.profile.path} element={<Profile/>}/>
          <Route path="*" element={<Navigate to={router.home.path} replace />} />
        </Routes>
      </Authenticated>
    </ErrorBoundary>
  );
};
